import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreateOrganisationDialog } from "@/components/CreateOrganisationDialog";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateTime } from "@/lib/format";
import { PlusIcon } from "lucide-react";

type Organisation = components["schemas"]["OrganisationAdminResponse"];

export function ClientOrganisationsPage() {
  const { clientId } = useParams<{ clientId: string }>();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      pagination.reset();
    }
  }, [debouncedSearch, pagination.reset]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    async function fetchOrganisations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET(
        "/admin/organisations",
        {
          params: {
            query: {
              ClientId: clientId!,
              Cursor: pagination.cursor,
              Limit: pagination.limit,
              Name: debouncedSearch || undefined,
            },
          },
        },
      );
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load organisations.");
      } else {
        setOrganisations(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [clientId, pagination.cursor, pagination.limit, debouncedSearch, refetchKey]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Organisations</h1>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <PlusIcon className="mr-2 size-4" />
          Create Organisation
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Search by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organisations.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground h-24 text-center">
                  {debouncedSearch
                    ? `No organisations matching "${debouncedSearch}".`
                    : "No organisations found."}
                </TableCell>
              </TableRow>
            ) : (
              organisations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">
                    <Link to={`/organisations/${org.id}`} className="hover:underline">
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${org.id}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-secondary/80">
                        {org.id}
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {org.clientId}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDateTime(org.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      {clientId && (
        <CreateOrganisationDialog
          clientId={clientId}
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreated={() => setRefetchKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
